import { useMemo } from 'react';
import {
  fatruckclassicregular,
  FontAwesomeIcon,
  GrcButton,
  GrcProgressBar,
  IconProp
} from '@grc/ui-package';
import { formatDateTimeWithTimeZone } from '@grc/ui-shared/lib/components/common/constants/constants';
import { useI18nState } from '../../../../stores/settings/language-context';
import './tracking-updates-drawer.scss';

interface ITrackingUpdatesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  shipmentNumber: string;
  stops: any[];
  statusUpdates: any[];
}

// Trimmed, read-only port of the reference Order app's TrackingDrawer
// (manage-order-tabs/tracking-drawer) - progress bar, ETA banner, stop/
// status timeline, Close/View on Map. The reference's "Manual Update"
// button and CheckCallDrawer (check-call entry) are intentionally dropped -
// that's an edit/mutation flow, out of scope for this read-only screen.
export default function TrackingUpdatesDrawer({
  isOpen,
  onClose,
  shipmentNumber,
  stops,
  statusUpdates
}: ITrackingUpdatesDrawerProps) {
  const { localeData } = useI18nState();

  const progress = useMemo(() => {
    const stopItems = stops.filter((s: any) => s?.type === 'stop');
    if (!stopItems.length) return 0;

    const latestStop = stopItems
      .filter((s: any) => s?.isDeparted)
      .sort((a: any, b: any) => (b?.stopSequence ?? 0) - (a?.stopSequence ?? 0))[0];
    if (latestStop) return 100;

    const totalProgress = stopItems.reduce((acc: number, stop: any) => {
      if (stop.isDeparted) return acc + 1;
      if (stop.isArrived) return acc + 0.5;
      return acc;
    }, 0);

    return (totalProgress / stopItems.length) * 100;
  }, [stops]);

  if (!isOpen) return null;

  const stopLabel: Record<string, string> = {
    inTransit: localeData.InTransit,
    prePickup: localeData.PrePickup,
    Arrival: localeData.Arrival,
    Departure: localeData.Departure,
    info: localeData.Info
  };

  return (
    <div className="tracking-updates-drawer-container">
      <div className="tracking-updates-drawer-overlay" onClick={onClose} />
      <div className="tracking-updates-drawer">
        <div className="tracking-updates-drawer-header">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0">
              {localeData.TrackingUpdates} - {shipmentNumber}
            </h5>
            <button
              className="btn-close"
              aria-label={localeData.Close}
              onClick={onClose}
            />
          </div>
          <div className="tracking-updates-progress-section">
            <GrcProgressBar
              min={0}
              max={100}
              value={progress}
              width="100%"
              id="tracking-updates-progress"
            />
            <div
              className="tracking-updates-truck-icon"
              style={{ '--progress-value': progress } as React.CSSProperties}
            >
              <FontAwesomeIcon icon={fatruckclassicregular as IconProp} />
            </div>
          </div>
        </div>

        <div className="tracking-updates-timeline">
          {statusUpdates.length === 0 && (
            <div className="d-flex align-items-center justify-content-center">
              {localeData.NoCheckCallstoShow}
            </div>
          )}
          {statusUpdates.map((update: any, index: number) => (
            <div
              key={update?.id ?? `${update?.stopSequence}-${index}`}
              className={`tracking-updates-stop ${update?.isLatest ? 'most-recent-stop' : ''}`}
            >
              <div className="tracking-updates-stop-icon">
                <span className="tracking-updates-dot" />
                {index < statusUpdates.length - 1 && (
                  <div className="tracking-updates-line" />
                )}
              </div>
              <div className="tracking-updates-stop-content">
                <span className="mb-1 stop-location text-sx">
                  {stopLabel[update?.type] ?? update?.type}
                </span>
                {update?.isLatest && (
                  <span className="ml-2 text-sx most-recent">
                    {localeData.MostRecent}
                  </span>
                )}
                <div className="d-flex align-items-start grc-text-small grc-text-light">
                  <span>
                    {localeData.DateSlashTime} -{' '}
                    {update?.locationUpdateOccurredOn
                      ? formatDateTimeWithTimeZone(
                          update.locationUpdateOccurredOn,
                          update?.reportedInTimeZone
                        )
                      : '-'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="tracking-updates-drawer-footer">
          <div className="d-flex align-items-center justify-content-end">
            <GrcButton
              id="tracking_updates_close_button"
              text={localeData.Close}
              className="grc-btn-secondary"
              onClick={onClose}
            />
            <GrcButton
              id="tracking_updates_view_on_map_button"
              className="grc-btn-primary ms-2"
              text={localeData.ViewOnMap}
              disabled
            />
          </div>
        </div>
      </div>
    </div>
  );
}
